import React, { useState, useEffect } from 'react';
import { FileText, Activity, TrendingUp, Factory, Boxes, BookOpen, Calculator, CheckCircle, AlertCircle, Wrench, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';

const MainDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    financialMetrics: [],
    operationalMetrics: [],
    alerts: [],
    recentActivities: [],
    companyInfo: {
      establishedYear: '2020',
      employees: 0,
      revenue: 'HK$ 0'
    },
    journalSummary: {
      total_entries: 0,
      total_posted: 0,
      total_draft: 0,
      total_review: 0,
      total_amount_posted: 0,
      balance_verified: true
    },
    accountBalancesSummary: {
      total_assets: 0,
      total_liabilities: 0,
      total_equity: 0,
      net_income: 0,
      balance_check: true
    },
    productionSummary: {
      active_orders: 0,
      overall_completion_rate: 0,
      quality_pass_rate: 0,
      capacity_utilization: 0,
      efficiency_rate: 0
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch financial metrics from chart of accounts
      const { data: chartData, error: chartError } = await supabase
        .from('chart_of_accounts')
        .select('*');

      if (chartError) throw chartError;

      // Fetch journal entries
      const { data: journalData, error: journalError } = await supabase
        .from('financial_journal_entries')
        .select('*');

      if (journalError) throw journalError;

      // Fetch production orders
      const { data: productionData, error: productionError } = await supabase
        .from('production_orders')
        .select('*');

      if (productionError) throw productionError;

      // Fetch employees count
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('*');

      if (employeesError) throw employeesError;

      // Fetch journal lines to calculate actual balances
      const { data: journalLinesData, error: journalLinesError } = await supabase
        .from('financial_journal_lines')
        .select('*');

      if (journalLinesError) throw journalLinesError;

      // Calculate financial metrics from journal lines
      const assets = chartData?.filter(acc => acc.account_type === 'Asset') || [];
      const liabilities = chartData?.filter(acc => acc.account_type === 'Liability') || [];
      const equity = chartData?.filter(acc => acc.account_type === 'Equity') || [];
      const revenue = chartData?.filter(acc => acc.account_type === 'Revenue') || [];
      const expenses = chartData?.filter(acc => acc.account_type === 'Expense') || [];

      // Calculate balances from journal lines
      const calculateAccountBalance = (accountCode, accountType) => {
        const lines = journalLinesData?.filter(line => line.account_code === accountCode) || [];
        const debits = lines.reduce((sum, line) => sum + (parseFloat(line.debit_amount) || 0), 0);
        const credits = lines.reduce((sum, line) => sum + (parseFloat(line.credit_amount) || 0), 0);
        
        // Assets and Expenses have debit balances, Liabilities/Equity/Revenue have credit balances
        if (accountType === 'Asset' || accountType === 'Expense') {
          return debits - credits;
        } else {
          return credits - debits;
        }
      };

      const totalAssets = assets.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);
      const totalLiabilities = liabilities.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);
      const totalEquity = equity.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);
      const totalRevenue = revenue.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);
      const totalExpenses = expenses.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);

      // Calculate journal summary
      const totalEntries = journalData?.length || 0;
      const postedEntries = journalData?.filter(entry => entry.status === 'posted').length || 0;
      const draftEntries = journalData?.filter(entry => entry.status === 'draft').length || 0;
      const reviewEntries = journalData?.filter(entry => entry.status === 'review').length || 0;
      const totalAmountPosted = journalData?.filter(entry => entry.status === 'posted')
        .reduce((sum, entry) => sum + (parseFloat(entry.total_debit) || 0), 0);

      // Calculate production summary
      const activeOrders = productionData?.filter(order => order.status === 'in_progress').length || 0;
      const completedOrders = productionData?.filter(order => order.status === 'completed').length || 0;
      const totalOrders = productionData?.length || 0;
      const completionRate = totalOrders > 0 ? completedOrders / totalOrders : 0;

      // Generate alerts based on data
      const alerts = [];
      if (totalEntries === 0) {
        alerts.push({
          type: 'warning',
          message: 'No journal entries found',
          priority: 'medium',
          count: 1
        });
      }
      if (activeOrders === 0) {
        alerts.push({
          type: 'info',
          message: 'No active production orders',
          priority: 'low',
          count: 1
        });
      }

      // Generate recent activities
      const recentActivities = [];
      if (journalData && journalData.length > 0) {
        const latestJournal = journalData[0];
        recentActivities.push({
          type: 'financial',
          activity: `Journal entry ${latestJournal.entry_number} created`,
          time: new Date(latestJournal.created_at).toLocaleDateString(),
          user: 'System'
        });
      }

      setDashboardData({
        financialMetrics: [
          {
            label: 'Total Assets',
            value: `HK$${(totalAssets || 0).toLocaleString()}`,
            change: '+5.2%',
            positive: true
          },
          {
            label: 'Total Liabilities',
            value: `HK$${(totalLiabilities || 0).toLocaleString()}`,
            change: '+2.1%',
            positive: false
          },
          {
            label: 'Net Income',
            value: `HK$${((totalRevenue || 0) - (totalExpenses || 0)).toLocaleString()}`,
            change: '+8.7%',
            positive: true
          },
          {
            label: 'Cash Flow',
            value: `HK$${(totalAssets || 0).toLocaleString()}`,
            change: '+3.4%',
            positive: true
          }
        ],
        operationalMetrics: [
          {
            label: 'Active Orders',
            value: activeOrders.toString(),
            change: '+12%',
            positive: true
          },
          {
            label: 'Production Efficiency',
            value: `${Math.round(completionRate * 100)}%`,
            change: '+5%',
            positive: true
          },
          {
            label: 'Quality Rate',
            value: '98.5%',
            change: '+1.2%',
            positive: true
          }
        ],
        alerts,
        recentActivities,
        companyInfo: {
          establishedYear: '2020',
          employees: employeesData?.length || 0,
          revenue: `HK$${(totalRevenue || 0).toLocaleString()}`
        },
        journalSummary: {
          total_entries: totalEntries,
          total_posted: postedEntries,
          total_draft: draftEntries,
          total_review: reviewEntries,
          total_amount_posted: totalAmountPosted,
          balance_verified: totalEntries > 0
        },
        accountBalancesSummary: {
          total_assets: totalAssets,
          total_liabilities: totalLiabilities,
          total_equity: totalEquity,
          net_income: totalRevenue - totalExpenses,
          balance_check: Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01
        },
        productionSummary: {
          active_orders: activeOrders,
          overall_completion_rate: completionRate,
          quality_pass_rate: 0.985,
          capacity_utilization: 0.75,
          efficiency_rate: completionRate
        }
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-md p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">歡迎使用 ERP 系統</h1>
            <p className="text-base text-blue-100 mb-2">企業資源管理全方位解決方案</p>
            <div className="flex items-center space-x-4 text-sm">
              <span>成立: {dashboardData.companyInfo.establishedYear}</span>
              <span>員工: {dashboardData.companyInfo.employees}人</span>
              <span>年營收: {dashboardData.companyInfo.revenue}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-100">今天是</p>
            <p className="text-lg font-semibold">{new Date().toLocaleDateString('zh-TW')}</p>
            <p className="text-sm text-blue-100">會計期間: 2025-01</p>
          </div>
        </div>
      </div>

      {/* Enhanced Financial Metrics with Chart of Accounts Integration */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardData.financialMetrics.map((metric, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{metric.label}</p>
                <p className="text-xl font-bold text-gray-800">{metric.value}</p>
              </div>
              <div className={`text-sm ${metric.positive ? 'text-green-600' : 'text-red-600'}`}>
                {metric.change}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Journal Entries Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">Journal Entries Summary</h2>
              <p className="text-base text-gray-600">日記帳分錄概況</p>
            </div>
          </div>
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
            dashboardData.journalSummary.balance_verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {dashboardData.journalSummary.balance_verified ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-medium text-sm">
              {dashboardData.journalSummary.balance_verified ? 'Balance Verified' : 'Balance Error'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-blue-600 text-sm mb-1">Total Entries</p>
            <p className="text-xl font-bold text-blue-800">{dashboardData.journalSummary.total_entries}</p>
            <p className="text-xs text-blue-600">本期分錄總數</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-green-600 text-sm mb-1">Posted Entries</p>
            <p className="text-xl font-bold text-green-800">{dashboardData.journalSummary.total_posted}</p>
            <p className="text-xs text-green-600">已過帳分錄</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <p className="text-yellow-600 text-sm mb-1">Pending</p>
            <p className="text-xl font-bold text-yellow-800">{dashboardData.journalSummary.total_draft + dashboardData.journalSummary.total_review}</p>
            <p className="text-xs text-yellow-600">待處理分錄</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-purple-600 text-sm mb-1">Posted Amount</p>
            <p className="text-xl font-bold text-purple-800">HK${(dashboardData.journalSummary.total_amount_posted || 0).toLocaleString()}</p>
            <p className="text-xs text-purple-600">已過帳金額</p>
          </div>
        </div>
      </div>

      {/* Chart of Accounts Summary */}
      <div 
        className="bg-gradient-to-r from-slate-800 to-slate-600 rounded-lg shadow-lg overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=300&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="bg-gradient-to-r from-black/80 to-black/60 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Calculator className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-bold">Financial Position Summary</h2>
                <p className="text-base text-slate-300">會計科目餘額總覽</p>
              </div>
            </div>
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              dashboardData.accountBalancesSummary.balance_check ? 'bg-green-500 bg-opacity-20 text-green-300' : 'bg-red-500 bg-opacity-20 text-red-300'
            }`}>
              {dashboardData.accountBalancesSummary.balance_check ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span className="font-medium text-sm">
                {dashboardData.accountBalancesSummary.balance_check ? 'Balance Verified' : 'Balance Error'}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-slate-300 text-sm mb-1">Total Assets</p>
              <p className="text-xl font-bold">HK${(dashboardData.accountBalancesSummary.total_assets || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-slate-300 text-sm mb-1">Total Liabilities</p>
              <p className="text-xl font-bold">HK${(dashboardData.accountBalancesSummary.total_liabilities || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-slate-300 text-sm mb-1">Total Equity</p>
              <p className="text-xl font-bold">HK${(dashboardData.accountBalancesSummary.total_equity || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-slate-300 text-sm mb-1">Net Income</p>
              <p className="text-xl font-bold text-green-300">HK${(dashboardData.accountBalancesSummary.net_income || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Production Management Summary */}
      <div 
        className="bg-gradient-to-r from-orange-600 to-red-600 rounded-lg shadow-lg overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=1200&h=300&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="bg-gradient-to-r from-black/80 to-black/60 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Factory className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-bold">Production Management Summary</h2>
                <p className="text-base text-orange-200">生產管理概況</p>
              </div>
            </div>
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              dashboardData.productionSummary.efficiency_rate >= 0.9 ? 'bg-green-500 bg-opacity-20 text-green-300' : 'bg-yellow-500 bg-opacity-20 text-yellow-300'
            }`}>
              {dashboardData.productionSummary.efficiency_rate >= 0.9 ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span className="font-medium text-sm">
                {Math.round(dashboardData.productionSummary.efficiency_rate * 100)}% 效率
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-orange-200 text-sm mb-1">Active Orders</p>
              <p className="text-xl font-bold">{dashboardData.productionSummary.active_orders}</p>
              <p className="text-xs text-orange-200">進行中訂單</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-orange-200 text-sm mb-1">Completion Rate</p>
              <p className="text-xl font-bold">{Math.round(dashboardData.productionSummary.overall_completion_rate * 100)}%</p>
              <p className="text-xs text-orange-200">完成率</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-orange-200 text-sm mb-1">Quality Pass Rate</p>
              <p className="text-xl font-bold text-green-300">{Math.round(dashboardData.productionSummary.quality_pass_rate * 100)}%</p>
              <p className="text-xs text-orange-200">品質合格率</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <p className="text-orange-200 text-sm mb-1">Capacity Utilization</p>
              <p className="text-xl font-bold">{Math.round(dashboardData.productionSummary.capacity_utilization * 100)}%</p>
              <p className="text-xs text-orange-200">產能利用率</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Operational Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardData.operationalMetrics.map((metric, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{metric.label}</p>
                <p className="text-xl font-bold text-gray-800">{metric.value}</p>
              </div>
              <div className={`text-sm ${metric.positive ? 'text-green-600' : 'text-red-600'}`}>
                {metric.change}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Enhanced Alerts and Recent Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Alerts */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">系統提醒</h2>
          <div className="space-y-3">
            {dashboardData.alerts.map((alert, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                alert.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                alert.type === 'error' ? 'bg-red-50 border border-red-200' :
                alert.type === 'success' ? 'bg-green-50 border border-green-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm ${
                    alert.type === 'warning' ? 'text-yellow-800' :
                    alert.type === 'error' ? 'text-red-800' :
                    alert.type === 'success' ? 'text-green-800' :
                    'text-blue-800'
                  }`}>
                    {alert.message}
                  </span>
                  <span className={`text-xs px-1 py-0.5 rounded ${
                    alert.priority === 'high' ? 'bg-red-200 text-red-800' :
                    alert.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {alert.priority === 'high' ? '高' : alert.priority === 'medium' ? '中' : '低'}
                  </span>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  alert.type === 'warning' ? 'bg-yellow-200 text-yellow-800' :
                  alert.type === 'error' ? 'bg-red-200 text-red-800' :
                  alert.type === 'success' ? 'bg-green-200 text-green-800' :
                  'bg-blue-200 text-blue-800'
                }`}>
                  {alert.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">最近活動</h2>
          <div className="space-y-3">
            {dashboardData.recentActivities.map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'sales' ? 'bg-green-500' :
                  activity.type === 'purchase' ? 'bg-blue-500' :
                  activity.type === 'production' ? 'bg-orange-500' :
                  'bg-purple-500'
                }`}></div>
                <div className="flex-1">
                  <p className={`text-sm text-gray-800`}>{activity.activity}</p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>{activity.time}</span>
                    <span>•</span>
                    <span>{activity.user}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">快速操作</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickActionButton
            icon={FileText}
            label="新增報價單"
            color="blue"
            onClick={() => console.log('新增報價單')}
          />
          <QuickActionButton
            icon={TrendingUp}
            label="查看銷售訂單"
            color="green"
            onClick={() => console.log('查看銷售訂單')}
          />
          <QuickActionButton
            icon={Factory}
            label="生產排程"
            color="orange"
            onClick={() => console.log('生產排程')}
          />
          <QuickActionButton
            icon={Boxes}
            label="庫存盤點"
            color="purple"
            onClick={() => console.log('庫存盤點')}
          />
        </div>
      </div>

      {/* Enhanced System Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">系統狀態</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">系統正常</p>
              <p className="text-xs text-green-600">所有服務運行正常</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
            <Shield className="w-6 h-6 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-800">安全狀態</p>
              <p className="text-xs text-blue-600">系統安全防護正常</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg">
            <Wrench className="w-6 h-6 text-yellow-600" />
            <div>
              <p className="text-sm font-medium text-yellow-800">維護提醒</p>
              <p className="text-xs text-yellow-600">下次維護: 2025-02-01</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg">
            <Activity className="w-6 h-6 text-purple-600" />
            <div>
              <p className="text-sm font-medium text-purple-800">系統負載</p>
              <p className="text-xs text-purple-600">CPU: 45% | Memory: 62%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickActionButton = ({ icon: Icon, label, color, onClick }) => {
  
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
    green: 'bg-green-50 text-green-600 hover:bg-green-100',
    orange: 'bg-orange-50 text-orange-600 hover:bg-orange-100',
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
  };

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center space-y-2 p-4 rounded-lg transition-colors ${colorClasses[color]}`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
};

export default MainDashboard; 